import React, { useEffect, useState } from 'react';
import Calling from '../api/Calling';
import { HiOutlineChip, HiOutlineDatabase, HiOutlineServer } from 'react-icons/hi';

const LoadChecker = ({ isMobile = false, isCompact = false }) => {
    const [loadData, setLoadData] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const resp = await Calling.GetServerLoadCheck();
                setLoadData(resp);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching load data:', error);
                setIsLoading(false);
            }
        };

        fetchData();
        const intervalId = setInterval(fetchData, 2000);
        return () => clearInterval(intervalId);
    }, []);

    // Dynamic status based on load value
    const getLoadStatus = (value) => {
        if (value === null || value === undefined) return 'loading';
        if (value < 50) return 'good';
        if (value <= 80) return 'warning';
        return 'critical';
    };

    // Color configurations
    const statusColors = {
        good: {
            text: 'text-emerald-600',
            bg: 'bg-emerald-500',
            bgLight: 'bg-emerald-50',
            border: 'border-emerald-200',
            gradient: 'from-emerald-500 to-emerald-600'
        },
        warning: {
            text: 'text-amber-600',
            bg: 'bg-amber-500',
            bgLight: 'bg-amber-50',
            border: 'border-amber-200',
            gradient: 'from-amber-500 to-orange-500'
        },
        critical: {
            text: 'text-red-600',
            bg: 'bg-red-500',
            bgLight: 'bg-red-50',
            border: 'border-red-200',
            gradient: 'from-red-500 to-red-600'
        },
        loading: {
            text: 'text-gray-400',
            bg: 'bg-gray-300',
            bgLight: 'bg-gray-50',
            border: 'border-gray-200',
            gradient: 'from-gray-300 to-gray-400'
        }
    };

    const loadItems = [
        { key: 'cpu', label: 'CPU', value: loadData.cpuLoad, icon: HiOutlineChip },
        { key: 'ram', label: 'RAM', value: loadData.ramUsage, icon: HiOutlineServer },
        { key: 'storage', label: 'Disk', value: loadData.storageUsage, icon: HiOutlineDatabase }
    ];

    // Get overall status
    const getOverallStatus = () => {
        if (loadItems.some(i => getLoadStatus(i.value) === 'critical')) return 'critical';
        if (loadItems.some(i => getLoadStatus(i.value) === 'warning')) return 'warning';
        if (loadItems.some(i => getLoadStatus(i.value) === 'loading')) return 'loading';
        return 'good';
    };

    // Compact version - just icons with values
    if (isCompact) {
        return (
            <div className="flex items-center gap-3">
                {loadItems.map((item) => {
                    const status = getLoadStatus(item.value);
                    const colors = statusColors[status];
                    const Icon = item.icon;

                    return (
                        <div key={item.key} className="flex items-center gap-2 group relative">
                            <div className={`
                                w-8 h-8 rounded-lg ${colors.bgLight} ${colors.border} border
                                flex items-center justify-center transition-all duration-200
                                group-hover:scale-110
                            `}>
                                <Icon className={`w-4 h-4 ${colors.text}`} />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] text-gray-400 uppercase tracking-wider leading-none">
                                    {item.label}
                                </span>
                                {item.value !== null && item.value !== undefined ? (
                                    <span className={`text-sm font-bold ${colors.text} leading-tight`}>
                                        {item.value}%
                                    </span>
                                ) : (
                                    <div className="w-8 h-4 bg-gray-200 rounded animate-pulse mt-0.5" />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // Mobile Grid version
    if (isMobile) {
        return (
            <div className="grid grid-cols-3 gap-2 w-full overflow-hidden">
                {loadItems.map((item) => {
                    const status = getLoadStatus(item.value);
                    const colors = statusColors[status];
                    const Icon = item.icon;

                    return (
                        <div
                            key={item.key}
                            className={`
                                relative p-2 rounded-xl ${colors.bgLight} border ${colors.border}
                                transition-all duration-300 min-w-0
                            `}
                        >
                            {/* Icon */}
                            <div className="w-7 h-7 rounded-lg bg-white shadow-sm flex items-center justify-center mb-1.5">
                                <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
                            </div>

                            {/* Value */}
                            {item.value !== null && item.value !== undefined ? (
                                <p className={`text-base font-bold ${colors.text} truncate`}>
                                    {item.value}%
                                </p>
                            ) : (
                                <div className="w-10 h-5 bg-white/50 rounded animate-pulse" />
                            )}

                            {/* Label */}
                            <p className="text-[10px] text-gray-500 mt-0.5 font-medium truncate">{item.label}</p>

                            {/* Progress bar */}
                            {item.value !== null && item.value !== undefined && (
                                <div className="mt-1.5 w-full h-1 bg-white rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full bg-gradient-to-r ${colors.gradient} transition-all duration-500`}
                                        style={{ width: `${item.value}%` }}
                                    />
                                </div>
                            )}

                            {/* Status indicator */}
                            <div className={`
                                absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full ${colors.bg}
                                ${status === 'critical' ? 'animate-pulse' : ''}
                            `} />
                        </div>
                    );
                })}
            </div>
        );
    }

    // Desktop version - Premium horizontal layout
    const overallStatus = getOverallStatus();
    const overallColors = statusColors[overallStatus];

    return (
        <div className="relative flex items-center gap-1 p-1.5 bg-gray-50/80 rounded-xl border border-gray-100">
            {/* Overall Status Indicator */}
            <div className={`
                absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full
                bg-gradient-to-b ${overallColors.gradient}
            `} />

            {loadItems.map((item, index) => {
                const status = getLoadStatus(item.value);
                const colors = statusColors[status];
                const Icon = item.icon;

                return (
                    <div
                        key={item.key}
                        className="group relative flex items-center gap-2 px-3 py-1.5 rounded-lg 
                                   hover:bg-white transition-all duration-200 cursor-default"
                    >
                        {/* Icon with background */}
                        <div className={`
                            relative w-8 h-8 rounded-lg ${colors.bgLight} border ${colors.border}
                            flex items-center justify-center transition-all duration-200 
                            group-hover:scale-105 group-hover:shadow-sm
                        `}>
                            <Icon className={`w-4 h-4 ${colors.text}`} />

                            {/* Pulse for critical */}
                            {status === 'critical' && (
                                <span className={`absolute inset-0 rounded-lg ${colors.bg} opacity-30 animate-ping`} />
                            )}
                        </div>

                        {/* Value and Label */}
                        <div className="flex flex-col min-w-[48px]">
                            <span className="text-[10px] text-gray-400 uppercase tracking-wider leading-none">
                                {item.label}
                            </span>
                            {item.value !== null && item.value !== undefined ? (
                                <span className={`text-sm font-bold ${colors.text} leading-tight`}>
                                    {item.value}%
                                </span>
                            ) : (
                                <div className="w-8 h-4 mt-0.5 bg-gray-200 rounded animate-pulse" />
                            )}
                        </div>

                        {/* Mini progress bar */}
                        {item.value !== null && item.value !== undefined && (
                            <div className="w-10 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full bg-gradient-to-r ${colors.gradient} transition-all duration-500`}
                                    style={{ width: `${item.value}%` }}
                                />
                            </div>
                        )}

                        {/* Hover tooltip */}
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-2 bg-gray-900 text-white 
                                        text-xs rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-200
                                        pointer-events-none whitespace-nowrap z-50 shadow-xl">
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${colors.bg}`} />
                                <span className="font-medium">{item.label}:</span>
                                <span>{item.value ?? '...'}%</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${colors.bgLight} ${colors.text}`}>
                                    {status === 'good' ? 'Normal' : status === 'warning' ? 'Moderate' : status === 'critical' ? 'High' : 'Loading'}
                                </span>
                            </div>
                            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>

                        {/* Divider */}
                        {index < loadItems.length - 1 && (
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-px h-6 bg-gray-200" />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default LoadChecker;